import { upperCaseFirst } from 'Util/StringUtils';
import { create } from 'zustand';
import { getAllLanguages } from 'Services/Api/_HumanWritten/LanguageService';
import { LanguageEntity } from 'Models/Entities';
import { whiteLabelStore } from '../Models/WhiteLabelStore';
import { isNotNullOrUndefined } from '../Util/TypeGuards';

const LANGUAGE_LOCAL_STORE_KEY = 'UserLanguageSettings';

interface LanguageStore {
	languages: LanguageEntity[] | null;
	selectedLanguage: LanguageEntity | null;
	setLanguages: (newLanguages: LanguageEntity[] | null) => void;
	setSelectedLanguage: (newLanguage: LanguageEntity | null) => void;
}

export const languageStore = create<LanguageStore>(set => ({
	languages: [],
	selectedLanguage: null,
	loading: false,
	setLanguages: (newLanguages: LanguageEntity[] | null) => set({ languages: newLanguages }),
	setSelectedLanguage: (newLanguage: LanguageEntity | null) => set({ selectedLanguage: newLanguage }),
}));

export const retrieveLanguagesAsync = async () => {
	const languages = await getAllLanguages();
	let defaultLanguage = languages.find(x => x.id === whiteLabelStore.config.defaultLanguageId) ?? languages[0];

	const languagePreferenceId = localStorage.getItem(LANGUAGE_LOCAL_STORE_KEY);

	if (isNotNullOrUndefined(languagePreferenceId)) {
		const preferredLanguage = languages.find(x => x.id === languagePreferenceId);
		if (preferredLanguage) {
			defaultLanguage = preferredLanguage;
		} else {
			localStorage.removeItem(LANGUAGE_LOCAL_STORE_KEY);
		}
	}

	languageStore.getState().setLanguages(languages);
	languageStore.getState().setSelectedLanguage(defaultLanguage);
};

export const getCurrentLanguageMessages = () => {
	let parsedTranslations: Record<string, { defaultMessage: string; description: string }> = {};

	try {
		parsedTranslations = JSON.parse(languageStore.getState().selectedLanguage?.translations ?? '{}');
	} catch (error) {
		console.error('Error parsing translations:', error);
	}

	return parsedTranslations;
};

export const getCurrentLanguageLocale = () => {
	return languageStore.getState().selectedLanguage?.locale ?? 'en';
};

export const getCurrentLanguageName = () => {
	return languageStore.getState().selectedLanguage?.name ?? 'English';
};

export const getLanguageCount = () => {
	return languageStore.getState().languages?.length ?? 0;
};

export const getLanguages = () => {
	return languageStore.getState().languages ?? [];
};

export const getMessageByKey = (key: string, defaultMessage?: string) => {
	const messages = getCurrentLanguageMessages();
	return messages[key]?.defaultMessage ?? defaultMessage ?? null;
};

export const getPascalCaseMessageByKey = (key: string, defaultMessage?: string) => {
	return upperCaseFirst(getMessageByKey(key, defaultMessage)?.toLowerCase());
};

export const getLowerCaseMessageByKey = (key: string, defaultMessage?: string) => {
	return getMessageByKey(key, defaultMessage)?.toLowerCase();
};

export const switchLanguagesAsync = async (id: string) => {
	const languages = await getAllLanguages();
	const newLanguage = languages.find(x => x.id === id) ?? languages[0];

	languageStore.getState().setLanguages(languages);
	languageStore.getState().setSelectedLanguage(newLanguage);
	localStorage.setItem(LANGUAGE_LOCAL_STORE_KEY, newLanguage.id);
};
